import { useEffect, useRef, useState } from "react";
import FilmConsole from "@tour/FilmConsole.jsx";

const SRC = "/videos/sales-demo.mp4";
const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5];
/** Master export frame — keep highlights and edges intact. */
const FRAME_W = 1920;
const FRAME_H = 1080;

export default function FilmPlayer() {
  const boxRef = useRef(null);
  const stageRef = useRef(null);
  const videoRef = useRef(null);
  const [ready, setReady] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [fs, setFs] = useState(false);
  const [frameRatio, setFrameRatio] = useState(FRAME_W / FRAME_H);

  useEffect(() => {
    fetch(SRC, { method: "HEAD" })
      .then((response) => {
        const type = (response.headers.get("content-type") || "").toLowerCase();
        setReady(response.ok && type.includes("video"));
      })
      .catch(() => setReady(false));
  }, []);

  useEffect(() => {
    function fitStage() {
      const stage = stageRef.current;
      const box = boxRef.current;
      if (!stage || !box) return;

      const vv = window.visualViewport;
      const consoleH = box.querySelector(".film-console")?.getBoundingClientRect().height || 0;
      const pad = 16;

      /* Prefer the visible viewport so browser zoom cannot crop the frame. */
      const availW = Math.max(240, (vv?.width ?? window.innerWidth) - pad * 2);
      const availH = Math.max(
        180,
        (vv?.height ?? window.innerHeight) - (document.fullscreenElement === box ? consoleH + pad : 0) - pad * 2
      );

      const ratio = frameRatio || FRAME_W / FRAME_H;
      let width = availW;
      let height = width / ratio;
      if (height > availH) {
        height = availH;
        width = height * ratio;
      }

      /* Inline (non-FS): cap height so the demo page still scrolls calmly. */
      if (document.fullscreenElement !== box) {
        const pageCap = Math.min(availH, window.innerHeight * 0.7);
        if (height > pageCap) {
          height = pageCap;
          width = height * ratio;
        }
        const hostW = box.clientWidth || availW;
        if (width > hostW) {
          width = hostW;
          height = width / ratio;
        }
      }

      stage.style.maxHeight = "none";
      stage.style.width = `${Math.floor(width)}px`;
      stage.style.height = `${Math.floor(height)}px`;
      box.style.setProperty("--film-vw", `${vv?.width ?? window.innerWidth}px`);
      box.style.setProperty("--film-vh", `${vv?.height ?? window.innerHeight}px`);
    }

    fitStage();
    const vv = window.visualViewport;
    window.addEventListener("resize", fitStage);
    vv?.addEventListener("resize", fitStage);
    vv?.addEventListener("scroll", fitStage);
    document.addEventListener("fullscreenchange", fitStage);
    return () => {
      window.removeEventListener("resize", fitStage);
      vv?.removeEventListener("resize", fitStage);
      vv?.removeEventListener("scroll", fitStage);
      document.removeEventListener("fullscreenchange", fitStage);
    };
  }, [ready, frameRatio, fs]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    const onTime = () => setTime(video.currentTime);
    const onMeta = () => {
      setDuration(video.duration || 0);
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setFrameRatio(video.videoWidth / video.videoHeight);
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    const onFs = () => setFs(document.fullscreenElement === boxRef.current);
    video.addEventListener("timeupdate", onTime);
    video.addEventListener("loadedmetadata", onMeta);
    video.addEventListener("play", onPlay);
    video.addEventListener("pause", onPause);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      video.removeEventListener("timeupdate", onTime);
      video.removeEventListener("loadedmetadata", onMeta);
      video.removeEventListener("play", onPlay);
      video.removeEventListener("pause", onPause);
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, [ready]);

  useEffect(() => {
    function onKey(event) {
      const video = videoRef.current;
      if (!video || !ready) return;
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (event.key === " ") {
        event.preventDefault();
        togglePlay();
      }
      if (event.key === "ArrowRight") video.currentTime = Math.min(video.duration || 0, video.currentTime + 10);
      if (event.key === "ArrowLeft") video.currentTime = Math.max(0, video.currentTime - 10);
      if (event.key === "ArrowUp") {
        event.preventDefault();
        setVol(Math.min(1, video.volume + 0.05));
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setVol(Math.max(0, video.volume - 0.05));
      }
      if (event.key === "Home") video.currentTime = 0;
      if (event.key === "End" && video.duration) video.currentTime = video.duration;
      if (event.key === "m" || event.key === "M") toggleMute();
      if (event.key === "f" || event.key === "F") toggleFs();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ready]);

  async function enterPresentation() {
    const box = boxRef.current;
    if (!box) return;
    try {
      if (document.fullscreenElement !== box) await box.requestFullscreen?.();
    } catch {
      /* Browser may block without a gesture; stage still letterboxes. */
    }
  }

  function togglePlay() {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      enterPresentation();
      video.play();
    } else video.pause();
  }

  function setVol(next) {
    const video = videoRef.current;
    if (!video) return;
    setVolume(next);
    setMuted(next === 0);
    video.volume = next;
    video.muted = next === 0;
  }

  function toggleMute() {
    const video = videoRef.current;
    if (!video) return;
    const next = !video.muted;
    setMuted(next);
    video.muted = next;
  }

  function toggleFs() {
    const box = boxRef.current;
    if (!box) return;
    if (document.fullscreenElement === box) document.exitFullscreen?.();
    else box.requestFullscreen?.();
  }

  if (ready !== true) return null;

  return (
    <div className="film-player" ref={boxRef}>
      <div className="film-player__stage" ref={stageRef}>
        <video
          ref={videoRef}
          className="film-player__video"
          src={SRC}
          playsInline
          preload="metadata"
          onClick={togglePlay}
          onError={() => setReady(false)}
        />
      </div>
      <FilmConsole
        clock={time}
        duration={duration}
        playing={playing}
        onToggle={togglePlay}
        onSeek={(next) => {
          videoRef.current.currentTime = next;
          setTime(next);
        }}
        onSkip={(delta) => {
          const video = videoRef.current;
          video.currentTime = Math.max(0, Math.min(video.duration || 0, video.currentTime + delta));
        }}
        volume={volume}
        onVolume={setVol}
        muted={muted}
        onMute={toggleMute}
        speed={speed}
        onSpeed={(next) => {
          setSpeed(next);
          videoRef.current.playbackRate = next;
        }}
        speeds={SPEEDS}
        onFs={toggleFs}
        fs={fs}
        downloadHref={SRC}
      />
    </div>
  );
}
